import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { adminResetPassword } from '../../services/api';
import { LoaderWrapper } from '../../components';

const schema = yup.object({
  username: yup.string().required('Username is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const AdminPasswordReset = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      newPassword: '',
      confirmPassword: '',
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('Admin password reset attempt for user:', data.username);
      
      const requestData = {
        username: data.username,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
        adminId: localStorage.getItem('orgId'), // Add admin ID for audit trail
        adminUsername: localStorage.getItem('username') // Add admin username
      };
      
      const response = await adminResetPassword(requestData);
      
      if (response.data.success) {
        setSuccess(`Password successfully changed for user: ${data.username}`);
        toast.success(`Password successfully changed for user: ${data.username}`);
        
        // Reset form after successful password change
        reset();
      } else {
        setError(response.data.message || 'Password change failed');
        toast.error(response.data.message || 'Password change failed');
      }
    } catch (error) {
      console.error('Admin password reset error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to change password. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePassword = (field) => {
    switch (field) {
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    reset();
    setError('');
    setSuccess('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <VpnKeyIcon sx={{ fontSize: 48, color: '#00024c', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#00024c', fontWeight: 600 }}>
            Admin Password Reset
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Reset password for any user in the system
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Password Requirements */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Password Requirements:</strong>
            <br />• At least 8 characters long
            <br />• Include uppercase and lowercase letters
            <br />• Include at least one number
            <br />• Include at least one special character (@$!%*?&)
          </Typography>
        </Alert>

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Username Field */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Username"
                  placeholder="Enter the username to reset password"
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* New Password Field */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleTogglePassword('new')}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Confirm Password Field */}
          <Box sx={{ mb: 4 }}>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleTogglePassword('confirm')}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              type="button"
              variant="outlined"
              size="large"
              onClick={handleClear}
              disabled={loading}
              sx={{ minWidth: 140 }}
            >
              Clear Form
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              disabled={loading}
              sx={{ 
                minWidth: 200,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                }
              }}
            >
              <LoaderWrapper loading={loading} type="button">
                {loading ? 'Changing Password...' : 'Change Password'}
              </LoaderWrapper>
            </Button>
          </Box>
        </form>

        {/* Warning Notice */}
        <Alert severity="warning" sx={{ mt: 4 }}>
          <Typography variant="body2">
            <strong>Important:</strong> This action will immediately change the user's password. 
            The user will need to use the new password for their next login. Please ensure you 
            communicate the new password securely to the user.
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

export default AdminPasswordReset;